import { Dialog } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";
import { GlobalContext } from "../../context";
import UploadAnswers from "../Assignments/UploadAnswers";
import InstructorBlackboard from "../../components/Dashboard/InstructorBlackboard";
import { downloadSubmissionFile } from "../../utils/submissionFile";
import SubmissionFilePreview from "../../components/Dashboard/SubmissionFilePreview";

const SUBMISSION_ACCEPTED_FILES =
  ".pdf,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.json,.ipynb,.py,.jpg,.jpeg,.png,.gif,.webp,.mp4";

// ─── helpers ──────────────────────────────────────────────────────────────────

function safeDate(val) {
  if (!val) return null;
  // PHP DateTime serializes as { date: "YYYY-MM-DD HH:MM:SS.000000", ... }
  const raw = typeof val === "object" && val.date ? val.date : val;
  const d = typeof raw === "string" ? parseISO(raw.replace(" ", "T")) : new Date(raw);
  return isValid(d) ? d : null;
}

function getVimeoEmbedUrl(url) {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}?autoplay=1&title=0&byline=0&portrait=0` : null;
}

function openVideoOverlay(url) {
  const vimeoEmbed = getVimeoEmbedUrl(url);
  const playerHtml = vimeoEmbed
    ? `<iframe src="${vimeoEmbed}" style="flex:1;width:100%;border:none"
         allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`
    : `<video controls controlsList="nodownload" disablePictureInPicture
         oncontextmenu="event.preventDefault();" style="flex:1;width:100%;background:#000">
         <source src="${url}" type="video/mp4"/>
       </video>`;

  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, {
    width: "100%", height: "100vh", border: "none",
    position: "fixed", top: "0", left: "0", zIndex: "9999",
  });
  const html = `<html><head><style>
    body{margin:0;background:#000;display:flex;flex-direction:column;height:100vh}
    .bar{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;background:#111}
    .bar span{color:#fff;font-size:14px;font-family:sans-serif}
    .close{background:#d15424;color:#fff;border:none;padding:8px 18px;border-radius:6px;
      font-size:14px;cursor:pointer;font-weight:600}
  </style></head><body>
    <div class="bar">
      <span>Recording</span>
      <button class="close" onclick="window.parent.postMessage('close-video','*')">✕ Close</button>
    </div>
    ${playerHtml}
  </body></html>`;
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  const handler = (e) => {
    if (e.data === "close-video") {
      document.body.removeChild(iframe);
      window.removeEventListener("message", handler);
    }
  };
  window.addEventListener("message", handler);
}

// ─── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    live:     { label: "Live Now",  cls: "bg-red-500 text-white",    dot: true  },
    upcoming: { label: "Upcoming",  cls: "bg-blue-500 text-white",   dot: false },
    recorded: { label: "Recorded",  cls: "bg-green-500 text-white",  dot: false },
    ended:    { label: "Ended",     cls: "bg-gray-200 text-gray-600",dot: false },
  };
  const c = cfg[status?.toLowerCase?.()] || cfg.ended;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${c.cls}`}>
      {c.dot && <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
      {c.label}
    </span>
  );
}

// ─── Sidebar icons ────────────────────────────────────────────────────────────

function ChevronIcon({ open }) {
  return (
    <svg
      className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
    </svg>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function SidebarLesson({ lesson, selected, onSelect, indent = "pl-14" }) {
  const isSelected = selected?.type === "lesson" && selected?.data?.id === lesson.id;
  const st = lesson.status?.toLowerCase?.();

  const statusDot = {
    live:     "bg-red-500 animate-pulse",
    upcoming: "bg-blue-500",
    recorded: "bg-emerald-500",
    ended:    "bg-gray-300",
  }[st] || "bg-gray-300";

  return (
    <button
      onClick={() => onSelect({ type: "lesson", data: lesson })}
      className={`w-full text-left flex items-center gap-2.5 ${indent} pr-4 py-2.5 transition-all
        duration-150 relative ${
          isSelected
            ? "bg-[#d15424]/10 text-[#d15424] font-semibold"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        }`}
    >
      {isSelected && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-[#d15424] rounded-r-full" />
      )}
      <VideoIcon />
      <span className="truncate flex-1 text-sm">{lesson.topic || "Class Session"}</span>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot}`} />
      {st === "live" && (
        <span className="text-[9px] font-bold text-red-500 flex-shrink-0 tracking-wide">LIVE</span>
      )}
    </button>
  );
}

function SidebarWeek({ week, openWeeks, toggle, selected, onSelect }) {
  const isOpen = openWeeks.has(week.id);
  const schedules = week.schedules || [];

  return (
    <div>
      {/* Week row — indented to align past the module number badge */}
      <button
        onClick={() => toggle(week.id)}
        className="w-full text-left flex items-center gap-2 pl-11 pr-4 py-2 transition-colors
          hover:bg-gray-50 group"
      >
        <ChevronIcon open={isOpen} />
        <span className={`flex-1 text-xs font-semibold tracking-wide transition-colors ${
          isOpen ? "text-gray-800 font-bold" : "text-gray-500 group-hover:text-gray-700"
        }`}>
          Week {week.number}{week.title ? ` — ${week.title}` : ""}
        </span>
        <span className="text-[10px] font-medium text-gray-400 tabular-nums">
          {schedules.length}
        </span>
      </button>

      {isOpen && (
        <div className="border-l border-gray-100 ml-14 mb-0.5">
          {schedules.length === 0 ? (
            <p className="text-xs text-gray-400 pl-3 py-2 italic">No sessions yet</p>
          ) : (
            schedules.map((s) => (
              <SidebarLesson key={s.id} lesson={s} selected={selected} onSelect={onSelect} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SidebarModule({ mod, openMods, openWeeks, toggleMod, toggleWeek, selected, onSelect, index }) {
  const isOpen = openMods.has(mod.id);
  const directSessions = mod.direct_schedules || [];
  const totalSessions = (mod.weeks || []).reduce((n, w) => n + (w.schedules?.length || 0), 0)
    + directSessions.length;
  const totalProjects = (mod.projects || []).length;
  const totalAssessments = (mod.assessments || []).length;

  return (
    <div className={`border-b border-gray-100 last:border-0 ${isOpen ? "bg-slate-50/50" : ""}`}>
      {/* Module header */}
      <button
        onClick={() => toggleMod(mod.id)}
        className="w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50
          transition-colors group"
      >
        {/* Module number badge */}
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold
          flex-shrink-0 transition-all duration-200 ${
            isOpen
              ? "bg-[#d15424] text-white shadow-md shadow-[#d15424]/20"
              : "bg-gray-100 text-gray-500 group-hover:bg-[#d15424]/10 group-hover:text-[#d15424]"
          }`}>
          {index + 1}
        </span>

        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm leading-snug truncate transition-colors ${
            isOpen ? "text-gray-900 font-bold" : "text-gray-700 group-hover:text-gray-900"
          }`}>
            {mod.name}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {mod.weeks?.length > 0 ? `${mod.weeks.length}w · ` : ""}{totalSessions} session{totalSessions !== 1 ? "s" : ""}
            {totalProjects > 0 ? ` · ${totalProjects} project${totalProjects !== 1 ? "s" : ""}` : ""}
            {totalAssessments > 0 ? ` · ${totalAssessments} quiz${totalAssessments !== 1 ? "zes" : ""}` : ""}
          </p>
        </div>

        <span className={`text-gray-400 group-hover:text-gray-600 transition-colors ${isOpen ? "text-gray-650" : ""}`}>
          <ChevronIcon open={isOpen} />
        </span>
      </button>

      {/* Module body */}
      {isOpen && (
        <div className="pb-2">
          {/* Legacy sessions */}
          {directSessions.length > 0 && (
            <div>
              {directSessions.map((s) => (
                <SidebarLesson
                  key={s.id}
                  lesson={s}
                  selected={selected}
                  onSelect={onSelect}
                  indent="pl-11"
                />
              ))}
            </div>
          )}
          {/* Weeks → Sessions */}
          {(mod.weeks || []).map((w) => (
            <SidebarWeek
              key={w.id}
              week={w}
              openWeeks={openWeeks}
              toggle={toggleWeek}
              selected={selected}
              onSelect={onSelect}
            />
          ))}

          {/* Projects */}
          {totalProjects > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="pl-4 pb-1 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                Projects
              </p>
              {(Array.isArray(mod.projects) ? mod.projects : []).map((p) => {
                const isSelected = selected?.type === "project" && selected?.data?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => onSelect({ type: "project", data: p })}
                    className={`w-full text-left flex items-center gap-2.5 pl-11 pr-4 py-2.5
                      transition-all duration-150 relative ${
                        isSelected
                          ? "bg-[#294a93]/10 text-[#294a93] font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                  >
                    {isSelected && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-[#294a93] rounded-r-full" />
                    )}
                    <FolderIcon />
                    <span className="truncate text-sm flex-1">{p.name || p.title}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Assessments */}
          {totalAssessments > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="pl-4 pb-1 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                Assessments
              </p>
              {mod.assessments.map((a) => {
                const isSelected = selected?.type === "assessment" && selected?.data?.id === a.id;
                return (
                  <button
                    key={a.id}
                    onClick={() => onSelect({ type: "assessment", data: a })}
                    className={`w-full text-left flex items-center gap-2.5 pl-11 pr-4 py-2.5
                      transition-all duration-150 relative ${
                        isSelected
                          ? "bg-[#294a93]/10 text-[#294a93] font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                  >
                    {isSelected && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-[#294a93] rounded-r-full" />
                    )}
                    <ClipboardIcon />
                    <span className="truncate text-sm flex-1">{a.title}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main content panels ───────────────────────────────────────────────────────

function GracePeriodBox({ lessonId, exercise, onChanged }) {
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [localExercise, setLocalExercise] = useState(exercise);

  useEffect(() => { setLocalExercise(exercise); }, [exercise]);

  const graceActive = localExercise?.grace_until && new Date(localExercise.grace_until) > new Date();

  const authHeader = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
  };

  const grant = async () => {
    setBusy(true);
    try {
      authHeader();
      const { data } = await client.post(`/schedules/${lessonId}/exercise/grace`, { hours: Number(hours) });
      if (data?.exercise) setLocalExercise(data.exercise);
      toast.success(`Grace period granted for ${hours}h.`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not grant grace period.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      authHeader();
      const { data } = await client.delete(`/schedules/${lessonId}/exercise/grace`);
      if (data?.exercise) setLocalExercise(data.exercise);
      toast.success("Grace period cleared.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not clear grace period.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 bg-amber-50 border border-dashed border-amber-200 rounded-xl p-3">
      <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Grace Period</p>
      {graceActive ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-amber-800">
            Active until {new Date(localExercise.grace_until).toLocaleString()}
          </p>
          <button type="button" onClick={clear} disabled={busy}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50">
            {busy ? "…" : "End Grace Period Now"}
          </button>
        </div>
      ) : (
        <div>
          {!localExercise?.due_at && (
            <p className="text-xs text-amber-700 mb-2">
              This assignment has no deadline yet — granting a grace period will close it as of right now and let students submit for the window below.
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={168}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="w-20 border border-amber-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <span className="text-xs text-amber-700">hours</span>
            <button type="button" onClick={grant} disabled={busy || !hours}
              className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {busy ? "…" : "Grant Grace Period"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LessonPanel({ lesson, isNG, onUpload, user, onRefresh }) {
  const timeObj = isNG ? lesson.time?.wat : lesson.time?.est;
  const date = safeDate(timeObj);
  const label = isNG ? "WAT" : "EST";
  const st = lesson.status?.toLowerCase?.();
  const isStudent = !user?.is_instructor && !user?.is_superadmin;

  const [showEditModal, setShowEditModal] = useState(false);
  const [topic, setTopic] = useState(lesson.topic || "");
  const [description, setDescription] = useState(lesson.description || "");
  const [link, setLink] = useState(lesson.link || "");
  const [recordingLink, setRecordingLink] = useState(lesson.recording_link || "");
  const [saving, setSaving] = useState(false);

  // ── Materials add form (instructor/admin only) ───────────────────────────
  const [newMaterialName, setNewMaterialName] = useState("");
  const [newMaterialLink, setNewMaterialLink] = useState("");
  const [newMaterialFile, setNewMaterialFile] = useState(null);
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [deletingMaterialId, setDeletingMaterialId] = useState(null);

  const handleAddMaterial = async (e) => {
    e.preventDefault();
    if (!newMaterialName.trim()) { toast.error("Give the material a name."); return; }
    if (!newMaterialLink && !newMaterialFile) { toast.error("Add a file or a link."); return; }
    setAddingMaterial(true);
    try {
      const fd = new FormData();
      fd.append("name", newMaterialName);
      if (newMaterialLink) fd.append("link", newMaterialLink);
      if (newMaterialFile) fd.append("file", newMaterialFile);
      await client.post(`/schedules/${lesson.id}/materials`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Material added.");
      setNewMaterialName(""); setNewMaterialLink(""); setNewMaterialFile(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add material.");
    } finally {
      setAddingMaterial(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    setDeletingMaterialId(materialId);
    try {
      await client.delete(`/class-materials/${materialId}`);
      toast.success("Material removed.");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove material.");
    } finally {
      setDeletingMaterialId(null);
    }
  };

  // ── Assignment (exercise) form state — instructor/admin only ─────────────
  const [exTitle, setExTitle] = useState(lesson.exercise?.title || "");
  const [exDescription, setExDescription] = useState(lesson.exercise?.description || "");
  const [exReleaseAt, setExReleaseAt] = useState(lesson.exercise?.solution_release_at ? lesson.exercise.solution_release_at.substring(0, 16) : "");
  const [exDueAt, setExDueAt] = useState(lesson.exercise?.due_at ? lesson.exercise.due_at.substring(0, 16) : "");
  const [exProblemFile, setExProblemFile] = useState(null);
  const [exSolutionFile, setExSolutionFile] = useState(null);
  const [savingExercise, setSavingExercise] = useState(false);

  useEffect(() => {
    setExTitle(lesson.exercise?.title || "");
    setExDescription(lesson.exercise?.description || "");
    setExReleaseAt(lesson.exercise?.solution_release_at ? lesson.exercise.solution_release_at.substring(0, 16) : "");
    setExDueAt(lesson.exercise?.due_at ? lesson.exercise.due_at.substring(0, 16) : "");
    setExProblemFile(null);
    setExSolutionFile(null);
  }, [lesson]);

  const handleSaveExercise = async (e) => {
    e.preventDefault();
    if (!exTitle.trim()) { toast.error("Give the assignment a title."); return; }
    if (!lesson.exercise && !exProblemFile) { toast.error("Upload the assignment file."); return; }
    setSavingExercise(true);
    try {
      const fd = new FormData();
      fd.append("title", exTitle);
      if (exDescription) fd.append("description", exDescription);
      if (exReleaseAt) fd.append("solution_release_at", exReleaseAt);
      if (exDueAt) fd.append("due_at", exDueAt);
      if (exProblemFile) fd.append("problem_file", exProblemFile);
      if (exSolutionFile) fd.append("solution_file", exSolutionFile);
      await client.post(`/schedules/${lesson.id}/exercise`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Assignment saved.");
      setExProblemFile(null); setExSolutionFile(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save assignment.");
    } finally {
      setSavingExercise(false);
    }
  };

  // ── Attendance state ──────────────────────────────────────────────────────
  const [attended, setAttended]               = useState(false);
  const [attendanceWindowOpen, setWindowOpen] = useState(false);
  const [attendanceLoading, setAttLoading]    = useState(true);
  const [submittingAtt, setSubmittingAtt]     = useState(false);

  useEffect(() => {
    if (!isStudent) { setAttLoading(false); return; }
    const token = localStorage.getItem("nd-rest-tkn");
    fetch(`${process.env.REACT_APP_DOMAIN_URL || ""}/api/schedules/${lesson.id}/attendance-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setAttended(data.attended ?? false);
        setWindowOpen(data.window_open ?? false);
      })
      .catch(() => {})
      .finally(() => setAttLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  const handleMarkAttendance = async (silent = false) => {
    if (submittingAtt || attended) return;
    setSubmittingAtt(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      const res = await fetch(
        `${process.env.REACT_APP_DOMAIN_URL || ""}/api/schedules/${lesson.id}/attend`,
        { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const data = await res.json();
      if (res.ok || res.status === 200) {
        setAttended(true);
        toast.success("Attendance marked! +10 points 🎉");
      } else if (!silent) {
        toast.error(data.message || "Could not mark attendance.");
      }
      // silent failures (e.g. attendance window not open yet) are swallowed —
      // the student still joined the class, they just fall back to the
      // explicit "Mark Attendance" button in the Attendance section below.
    } catch {
      if (!silent) toast.error("Something went wrong.");
    } finally {
      setSubmittingAtt(false);
    }
  };

  useEffect(() => {
    setTopic(lesson.topic || "");
    setDescription(lesson.description || "");
    setLink(lesson.link || "");
    setRecordingLink(lesson.recording_link || "");
  }, [lesson]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("_method", "PATCH");
      formData.append("topic", topic);
      formData.append("description", description);
      formData.append("link", link);
      formData.append("recording_link", recordingLink);

      await client.post(`/schedules/${lesson.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Lesson updated successfully!");
      setShowEditModal(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update lesson.");
    } finally {
      setSaving(false);
    }
  };

  // Discussion / Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const res = await client.get(`/schedules/${lesson.id}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error("Failed to fetch comments", err);
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    setReplyTo(null);
    setNewComment("");
    setReplyText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await client.post(`/schedules/${lesson.id}/comments`, { comment: newComment });
      setComments((prev) => [...prev, { ...res.data, replies: [] }]);
      setNewComment("");
      toast.success("Comment posted!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to post comment.");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handlePostReply = async (e, parentId) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await client.post(`/schedules/${lesson.id}/comments`, {
        comment: replyText,
        parent_id: parentId
      });
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === parentId) {
            return { ...c, replies: [...(c.replies || []), res.data] };
          }
          return c;
        })
      );
      setReplyText("");
      setReplyTo(null);
      toast.success("Reply posted!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to post reply.");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await client.delete(`/comments/${id}`);
      toast.success("Comment deleted.");
      setComments((prev) => {
        return prev
          .filter((c) => c.id !== id)
          .map((c) => ({
            ...c,
            replies: (c.replies || []).filter((r) => r.id !== id),
          }));
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete comment.");
    }
  };

  const handleDownloadClassMaterial = (material) => {
    const token = localStorage.getItem("nd-rest-tkn");
    const name  = material.name || `material_${material.id}`;
    const url   = `${process.env.REACT_APP_DOMAIN_URL || ""}/api/class-materials/${material.id}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Could not download material."));
  };

  const actionButton = () => {
    if (st === "live") {
      return (
        <a
          href={lesson.link}
          target="_blank"
          rel="noreferrer"
          onClick={isStudent ? () => handleMarkAttendance(true) : undefined}
          className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white
            font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
        >
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Join Live Class
        </a>
      );
    }
    if (st === "upcoming") {
      return lesson.link ? (
        <a
          href={lesson.link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#294a93] hover:bg-[#1e3872] text-white
            font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
        >
          Join Class
        </a>
      ) : (
        <span className="text-sm text-gray-400 italic">Link will be shared before class</span>
      );
    }
    if (st === "recorded" && lesson.recording_link) {
      return (
        <button
          onClick={() => openVideoOverlay(lesson.recording_link)}
          className="inline-flex items-center gap-2 bg-[#d15424] hover:bg-[#b8451d] text-white
            font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
        >
          ▶ Watch Recording
        </button>
      );
    }
    return (
      <span className="text-sm text-gray-400 bg-gray-100 px-4 py-3 rounded-xl">
        Recording not available yet
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={lesson.status} />
              {user?.is_instructor && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-2.5 py-1.5 rounded-xl transition-colors cursor-pointer"
                >
                  ⚙ Edit Lesson
                </button>
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-800 mt-2 leading-snug">
              {lesson.topic || "Class Session"}
            </h2>
            {date && (
              <p className="text-sm text-gray-500 mt-1">
                {format(date, "EEEE, MMMM d, yyyy · h:mm a")} {label}
              </p>
            )}
          </div>
          {date && (
            <div className="flex-shrink-0 bg-gray-50 rounded-xl p-3 text-center min-w-[64px]">
              <p className="text-xs text-gray-400 font-medium uppercase">{format(date, "MMM")}</p>
              <p className="text-2xl font-bold text-[#001931] leading-none">{format(date, "d")}</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {actionButton()}
        </div>

        {/* Class Description & Student Notice */}
        <div className="mt-6 border-t border-gray-100 pt-6">
          <h3 className="font-semibold text-gray-700 text-sm mb-3">Class Details & Guidelines</h3>
          <div className="space-y-4">
            {lesson.description ? (
              <p className="text-sm text-gray-600 bg-slate-50 border border-slate-100 rounded-xl p-4 leading-relaxed italic">
                "{lesson.description}"
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">No description provided for this session.</p>
            )}

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-blue-900 flex flex-col gap-1">
              <p className="font-semibold text-xs uppercase tracking-wider text-blue-800">Important Student Notice</p>
              <p className="text-xs leading-relaxed">
                Please join the live class using the link provided. Remember to check the LMS for the most up-to-date schedule. If you are unable to join the session live, a recording of this class will be uploaded here <strong>in less than 24 hours</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* ── Attendance (students only) ─────────────────────────────────── */}
        {isStudent && (
          <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
              ✅ Attendance
            </h3>
            {attendanceLoading ? (
              <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
            ) : attended ? (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <span className="text-emerald-600 text-xl">✓</span>
                <div>
                  <p className="text-sm font-semibold text-emerald-700">Attendance Marked</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Your attendance for this class has been recorded.</p>
                </div>
              </div>
            ) : st === "live" && attendanceWindowOpen ? (
              <div className="flex items-center justify-between gap-4 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-orange-800">Class is Live — Mark Your Attendance</p>
                  <p className="text-xs text-orange-600 mt-0.5">The attendance window closes 120 minutes after class starts.</p>
                </div>
                <button
                  onClick={handleMarkAttendance}
                  disabled={submittingAtt}
                  className="flex-shrink-0 bg-[#d15424] hover:bg-[#b8451d] text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
                >
                  {submittingAtt ? "Marking…" : "Mark Attendance"}
                </button>
              </div>
            ) : st === "live" && !attendanceWindowOpen ? (
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-blue-800">Class is starting soon</p>
                <p className="text-xs text-blue-600 mt-0.5">Attendance button will appear when the class begins.</p>
              </div>
            ) : (st === "upcoming" || !st) ? (
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-blue-800">Attendance not yet available</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Mark your attendance when this class goes live. The window is open for <strong>120 minutes</strong> after the class starts — after that, it will be disabled.
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-gray-600">No attendance recorded</p>
                <p className="text-xs text-gray-500 mt-0.5">The attendance window for this class has closed.</p>
              </div>
            )}
          </div>
        )}

        {/* Class Materials — always visible */}
        <div className="mt-6 border-t border-gray-100 pt-6">
          <h3 className="font-semibold text-gray-700 text-sm mb-3">📄 Class Materials</h3>
          {(lesson.materials && lesson.materials.length > 0) ? (
            <div className="space-y-2">
              {lesson.materials.map((m) => (
                <div key={m.id} className="flex flex-col sm:flex-row gap-3 bg-gray-50 rounded-xl p-4 items-center justify-between">
                  <div className="flex items-center gap-3 w-full sm:w-auto min-w-0">
                    <span className="text-2xl text-blue-600 flex-shrink-0">📚</span>
                    <p className="font-medium text-gray-800 text-sm truncate">{m.name}</p>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto justify-end mt-2 sm:mt-0 flex-shrink-0">
                    {m.file_path ? (
                      <button
                        onClick={() => handleDownloadClassMaterial(m)}
                        className="text-xs font-semibold bg-[#294a93] hover:bg-[#1e3872] text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
                      >
                        ↓ Download
                      </button>
                    ) : (
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg transition-colors text-center inline-block"
                      >
                        ↗ Open Link
                      </a>
                    )}
                    {!isStudent && (
                      <button
                        onClick={() => handleDeleteMaterial(m.id)}
                        disabled={deletingMaterialId === m.id}
                        className="text-xs font-semibold text-red-500 hover:text-red-700 px-2 py-2 disabled:opacity-50"
                      >
                        {deletingMaterialId === m.id ? "…" : "Remove"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-4">
              <span className="text-2xl opacity-40">📂</span>
              <div>
                <p className="text-sm font-medium text-gray-500">No material uploaded yet</p>
                <p className="text-xs text-gray-400 mt-0.5">Your instructor will upload class resources here when available.</p>
              </div>
            </div>
          )}

          {!isStudent && (
            <form onSubmit={handleAddMaterial} className="mt-3 space-y-2 bg-white border border-dashed border-gray-200 rounded-xl p-3">
              <input
                type="text"
                value={newMaterialName}
                onChange={(e) => setNewMaterialName(e.target.value)}
                placeholder="Material name, e.g. Week 3 Slides"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
              />
              <input
                type="url"
                value={newMaterialLink}
                onChange={(e) => setNewMaterialLink(e.target.value)}
                placeholder="External link (optional)"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
              />
              <input
                type="file"
                onChange={(e) => setNewMaterialFile(e.target.files[0] || null)}
                className="w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#FEF0EB] file:text-[#F05A19] hover:file:bg-[#FDD8CC]"
              />
              <button
                type="submit"
                disabled={addingMaterial}
                className="w-full bg-[#F05A19]/10 hover:bg-[#F05A19]/20 disabled:opacity-50 text-[#F05A19] font-semibold py-2 rounded-xl text-sm transition-colors"
              >
                {addingMaterial ? "Adding…" : "+ Add Material"}
              </button>
            </form>
          )}
        </div>

        {/* Exercise / Assignment — always visible, like Class Materials above */}
        <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="font-semibold text-gray-700 text-sm mb-3">Assignment</h3>
            {lesson.exercise ? (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-gray-800 text-sm">{lesson.exercise.title}</p>
                {lesson.exercise.description && (
                  <p className="text-xs text-gray-500 mt-1">{lesson.exercise.description}</p>
                )}
                {lesson.exercise.due_at && (
                  <p className="text-xs text-[#d15424] font-semibold mt-1">
                    Due: {new Date(lesson.exercise.due_at).toLocaleString()}
                  </p>
                )}
                {lesson.exercise.grace_until && new Date(lesson.exercise.grace_until) > new Date() && (
                  <p className="text-xs text-amber-600 font-semibold mt-0.5">
                    Grace period active until {new Date(lesson.exercise.grace_until).toLocaleString()}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {lesson.exercise.problem_download_link && (
                    <a
                      href={lesson.exercise.problem_download_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50
                        text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      Download Assignment
                    </a>
                  )}
                  {lesson.exercise.solution_download_link &&
                    new Date(lesson.exercise.solution_release_at) < new Date() && (
                      <a
                        href={lesson.exercise.solution_download_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold bg-green-50 hover:bg-green-100
                          text-green-700 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Download Solution
                      </a>
                    )}
                  {isStudent && (
                    lesson.exercise.is_open_for_submission === false ? (
                      <button
                        disabled
                        className="text-xs font-semibold bg-gray-200 text-gray-500 px-3 py-1.5 rounded-lg cursor-not-allowed"
                      >
                        Submission Closed
                      </button>
                    ) : (
                      <button
                        onClick={() => onUpload(lesson.exercise.id)}
                        className="text-xs font-semibold bg-[#d15424] hover:bg-[#b8451d]
                          text-white px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Submit Answer
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : (
              isStudent && (
                <div className="flex items-center gap-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-4">
                  <span className="text-2xl opacity-40">📋</span>
                  <div>
                    <p className="text-sm font-medium text-gray-500">No assignment posted yet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Your instructor will post an assignment here when available.</p>
                  </div>
                </div>
              )
            )}

            {!isStudent && (
              <form onSubmit={handleSaveExercise} className="mt-3 space-y-2 bg-white border border-dashed border-gray-200 rounded-xl p-3">
                <input
                  type="text"
                  value={exTitle}
                  onChange={(e) => setExTitle(e.target.value)}
                  placeholder="Assignment title, e.g. Week 3 Problem Set"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
                />
                <textarea
                  value={exDescription}
                  onChange={(e) => setExDescription(e.target.value)}
                  placeholder="Description / instructions (optional)"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 resize-none"
                />
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    {lesson.exercise ? "Replace assignment file (optional)" : "Assignment file"}
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setExProblemFile(e.target.files[0] || null)}
                    className="w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#FEF0EB] file:text-[#F05A19] hover:file:bg-[#FDD8CC]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Solution file (optional)</label>
                  <input
                    type="file"
                    onChange={(e) => setExSolutionFile(e.target.files[0] || null)}
                    className="w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#FEF0EB] file:text-[#F05A19] hover:file:bg-[#FDD8CC]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Submission deadline (optional)</label>
                  <input
                    type="datetime-local"
                    value={exDueAt}
                    onChange={(e) => setExDueAt(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Solution release date (optional)</label>
                  <input
                    type="datetime-local"
                    value={exReleaseAt}
                    onChange={(e) => setExReleaseAt(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
                  />
                </div>
                <button
                  type="submit"
                  disabled={savingExercise}
                  className="w-full bg-[#F05A19]/10 hover:bg-[#F05A19]/20 disabled:opacity-50 text-[#F05A19] font-semibold py-2 rounded-xl text-sm transition-colors"
                >
                  {savingExercise ? "Saving…" : lesson.exercise ? "Update Assignment" : "+ Add Assignment"}
                </button>
              </form>
            )}

            {!isStudent && lesson.exercise && (
              <GracePeriodBox lessonId={lesson.id} exercise={lesson.exercise} onChanged={onRefresh} />
            )}
        </div>
      </div>

      {/* Classroom Discussion / Comments Box */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 flex flex-col gap-6">
        <div>
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            💬 Classroom Discussion
            {comments.length > 0 && (
              <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                {comments.length}
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Ask questions, start a thread, or discuss the class with your fellow students and instructors.
          </p>
        </div>

        {/* Post Comment Form */}
        <form onSubmit={handlePostComment} className="flex gap-2">
          <input
            type="text"
            placeholder="Type a comment or question..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={submittingComment}
            className="flex-1 border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
          />
          <button
            type="submit"
            disabled={submittingComment || !newComment.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-xl text-sm transition-all disabled:opacity-50 flex items-center justify-center cursor-pointer active:scale-95 whitespace-nowrap"
          >
            Post Comment
          </button>
        </form>

        {/* Discussion list */}
        {commentsLoading ? (
          <div className="flex flex-col gap-4 animate-pulse">
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="w-8 h-8 rounded-full bg-gray-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                  <div className="h-3 bg-gray-50 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-slate-50/60 border border-slate-100 rounded-xl p-4 flex flex-col gap-2">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={comment.user?.profile_img || "https://www.gravatar.com/avatar?d=mp"}
                      alt={comment.user?.name}
                      className="w-6 h-6 rounded-full object-cover border border-gray-200"
                    />
                    <span className="font-semibold text-xs text-gray-800">
                      {comment.user?.name || "Unknown Student"}
                    </span>
                    {comment.user?.is_instructor && (
                      <span className="bg-blue-100 text-blue-700 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider">
                        Instructor
                      </span>
                    )}
                    {comment.user?.is_superadmin && (
                      <span className="bg-red-100 text-red-700 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider">
                        Admin
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {/* Delete button */}
                  {(user?.id === comment.user_id || user?.is_instructor || user?.is_superadmin) && (
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>

                {/* Comment content */}
                <p className="text-sm text-gray-700 pl-8 leading-relaxed whitespace-pre-wrap">{comment.comment}</p>

                {/* Reply action button */}
                <div className="pl-8 flex items-center gap-4 text-xs text-gray-400 mt-1">
                  <button
                    onClick={() => {
                      setReplyTo(replyTo?.id === comment.id ? null : comment);
                      setReplyText("");
                    }}
                    className="hover:text-blue-600 transition-colors font-semibold cursor-pointer"
                  >
                    Reply
                  </button>
                </div>

                {/* Reply Form */}
                {replyTo?.id === comment.id && (
                  <form onSubmit={(e) => handlePostReply(e, comment.id)} className="pl-8 flex gap-2 mt-2">
                    <input
                      type="text"
                      placeholder={`Reply to ${comment.user?.firstname || "comment"}...`}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={submittingComment}
                      className="flex-1 border border-gray-200 py-2 px-3 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white text-gray-800"
                    />
                    <button
                      type="submit"
                      disabled={submittingComment || !replyText.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 py-2 rounded-lg text-xs transition-all disabled:opacity-50 cursor-pointer"
                    >
                      Send
                    </button>
                  </form>
                )}

                {/* Nested Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="pl-8 mt-3 space-y-3 border-l-2 border-gray-100">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="flex flex-col gap-1.5 bg-white/50 p-3 rounded-xl border border-gray-100/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img
                              src={reply.user?.profile_img || "https://www.gravatar.com/avatar?d=mp"}
                              alt={reply.user?.name}
                              className="w-5 h-5 rounded-full object-cover border border-gray-100"
                            />
                            <span className="font-semibold text-[11px] text-gray-800">
                              {reply.user?.name || "Unknown"}
                            </span>
                            {reply.user?.is_instructor && (
                              <span className="bg-blue-50 text-blue-700 text-[8px] font-bold px-1.5 py-0.2 rounded uppercase tracking-wider">
                                Instructor
                              </span>
                            )}
                            {reply.user?.is_superadmin && (
                              <span className="bg-red-50 text-red-700 text-[8px] font-bold px-1.5 py-0.2 rounded uppercase tracking-wider">
                                Admin
                              </span>
                            )}
                            <span className="text-[9px] text-gray-400">
                              {new Date(reply.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {/* Delete button for reply */}
                          {(user?.id === reply.user_id || user?.is_instructor || user?.is_superadmin) && (
                            <button
                              onClick={() => handleDeleteComment(reply.id)}
                              className="text-[10px] text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 pl-7 leading-relaxed whitespace-pre-wrap">{reply.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {comments.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <span className="text-3xl block mb-2">👋</span>
                <p className="text-xs font-semibold text-gray-500">No questions or comments yet.</p>
                <p className="text-[10px] mt-0.5">Start the discussion by asking a question above!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Class Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100 my-8">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <h2 className="text-lg font-bold text-gray-900">Edit Class Session</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4 max-h-[calc(100vh-12rem)] overflow-y-auto text-left">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Topic</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 resize-none bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Meeting Link</label>
                <input
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  required
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Recording Link</label>
                <input
                  type="url"
                  value={recordingLink}
                  onChange={(e) => setRecordingLink(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 bg-white"
                  placeholder="https://..."
                />
              </div>

              <div className="flex gap-4 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 px-4 bg-[#F05A19] hover:bg-[#d14d14] text-white rounded-xl text-sm font-semibold shadow-md shadow-orange-100 transition-colors disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function fileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (["jpg","jpeg","png","gif","webp","svg"].includes(ext)) return "🖼️";
  if (ext === "pdf") return "📕";
  if (["xls","xlsx"].includes(ext)) return "📊";
  if (["doc","docx"].includes(ext)) return "📝";
  if (["zip","rar","7z"].includes(ext)) return "🗜️";
  if (["mp4","mov","avi"].includes(ext)) return "🎬";
  return "📄";
}

function fileExt(name) {
  return (name || "").split(".").pop().toLowerCase();
}

function MaterialPreviewModal({ material, onClose }) {
  const name = material.name || material.file_name || material.file_path || "";
  const ext  = fileExt(name);
  const isImage = ["jpg","jpeg","png","gif","webp","svg"].includes(ext);
  const isPdf   = ext === "pdf";
  const previewUrl = material.url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh]
        flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100
          flex-shrink-0">
          <p className="font-semibold text-gray-800 text-sm truncate pr-4">{name}</p>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none flex-shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Preview body */}
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center min-h-0">
          {isImage && previewUrl ? (
            <img
              src={previewUrl}
              alt={name}
              className="max-w-full max-h-full object-contain p-4"
            />
          ) : isPdf && previewUrl ? (
            <iframe
              src={previewUrl}
              title={name}
              className="w-full h-full min-h-[500px]"
              style={{ border: "none" }}
            />
          ) : (
            <div className="text-center p-8">
              <div className="text-6xl mb-4">{fileIcon(name)}</div>
              <p className="text-gray-600 font-medium mb-1">{name}</p>
              <p className="text-sm text-gray-400">
                Preview not available for this file type.
              </p>
            </div>
          )}
        </div>

        {/* Footer download */}
        <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <a
            href={`${process.env.REACT_APP_DOMAIN_URL || ""}/api/project-materials/${material.id}/download`}
            className="inline-flex items-center gap-2 bg-[#d15424] hover:bg-[#b8451d] text-white
              font-semibold px-4 py-2 rounded-xl text-sm transition-colors"
            onClick={(e) => {
              const token = localStorage.getItem("nd-rest-tkn");
              if (!token) return;
              e.preventDefault();
              const url = `${process.env.REACT_APP_DOMAIN_URL || ""}/api/project-materials/${material.id}/download`;
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((blob) => {
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = name;
                  a.click();
                  URL.revokeObjectURL(a.href);
                })
                .catch(() => toast.error("Download failed."));
            }}
          >
            ↓ Download {ext.toUpperCase()}
          </a>
        </div>
      </div>
    </div>
  );
}

function ProjectPanel({ project }) {
  const [preview, setPreview] = useState(null);
  const materials = project.materials || [];

  const [submission, setSubmission] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSubmissionPreview, setShowSubmissionPreview] = useState(false);
  const [subForm, setSubForm] = useState({
    title: "",
    description: "",
    file_link: "",
  });
  const [subMode, setSubMode] = useState("link"); // "link" | "file"
  const [subFile, setSubFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const startedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  const closed = project.is_open_for_submission === false;
  const inGrace = project.grace_until && new Date(project.grace_until) > new Date();

  const fetchSubmission = async () => {
    try {
      setLoadingSubmission(true);
      const res = await client.get(`/projects/${project.id}/submission`);
      setSubmission(res.data.submission);
    } catch {
      setSubmission(null);
    } finally {
      setLoadingSubmission(false);
    }
  };

  useEffect(() => {
    fetchSubmission();
    setShowForm(false);
    setSubForm({
      title: "",
      description: "",
      file_link: "",
    });
    setSubMode("link");
    setSubFile(null);
    startedAt.current = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    if (!subForm.title.trim() || !subForm.description.trim()) {
      toast.error("Add a title and description.");
      return;
    }
    const hasFile = subMode === "file" && (subFile || submission?.file_path);
    if (subMode === "link" && !subForm.file_link.trim()) {
      toast.error("Add a link to your work.");
      return;
    }
    if (subMode === "file" && !hasFile) {
      toast.error("Choose a file to upload.");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (subMode === "file" && subFile) {
        const fd = new FormData();
        fd.append("title", subForm.title);
        fd.append("description", subForm.description);
        fd.append("file", subFile);
        fd.append("website", honeypot);
        fd.append("form_started_at", String(startedAt.current));
        res = await client.post(`/projects/${project.id}/submit`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await client.post(`/projects/${project.id}/submit`, {
          title: subForm.title,
          description: subForm.description,
          file_link: subMode === "link" ? subForm.file_link.trim() : undefined,
          website: honeypot,
          form_started_at: startedAt.current,
        });
      }
      toast.success("Project submitted successfully!");
      setSubmission(res.data.submission);
      setShowForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit project.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = (m) => {
    const token = localStorage.getItem("nd-rest-tkn");
    const name  = m.name || m.file_name || basename(m.file_path) || `file_${m.id}`;
    const url   = `${process.env.REACT_APP_DOMAIN_URL || ""}/api/project-materials/${m.id}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Could not download this file."));
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
        {preview && (
          <MaterialPreviewModal material={preview} onClose={() => setPreview(null)} />
        )}

        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#294a93]/10 flex items-center justify-center
            text-2xl flex-shrink-0">
            📁
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800">{project.name || project.title}</h2>
            {project.due_at && (
              <p className="text-sm text-[#d15424] mt-1 font-semibold">
                Due: {format(safeDate(project.due_at) || new Date(), "MMMM d, yyyy")}
              </p>
            )}
            {inGrace && (
              <p className="text-xs text-amber-600 mt-0.5 font-semibold">
                Grace period — submit by {format(safeDate(project.grace_until) || new Date(), "MMMM d, yyyy p")}
              </p>
            )}
            {closed && !inGrace && (
              <p className="text-xs text-gray-400 mt-0.5 font-semibold">Submission window closed</p>
            )}
          </div>
        </div>

        {project.description && (
          <p className="text-gray-600 text-sm mb-6 leading-relaxed whitespace-pre-wrap">{project.description}</p>
        )}

        <div>
          <h3 className="font-semibold text-gray-700 text-sm mb-3">Project Files</h3>
          {materials.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <p className="text-2xl mb-2">📭</p>
              <p className="text-sm text-gray-500">No files uploaded yet.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {materials.map((m) => {
                const name = m.name || m.file_name || (m.file_path || "").split("/").pop() || `File ${m.id}`;
                const ext  = fileExt(name);
                const canPreview = ["jpg","jpeg","png","gif","webp","svg","pdf"].includes(ext);
                return (
                  <div
                    key={m.id}
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 text-sm text-gray-700"
                  >
                    <span className="text-xl flex-shrink-0">{fileIcon(name)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{name}</p>
                      {m.description && (
                        <p className="text-xs text-gray-400 truncate">{m.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canPreview && (
                        <button
                          onClick={() => setPreview(m)}
                          className="text-xs font-semibold text-[#294a93] hover:underline"
                        >
                          Preview
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(m)}
                        className="text-xs font-semibold bg-[#d15424] hover:bg-[#b8451d] text-white
                          px-3 py-1.5 rounded-lg transition-colors"
                      >
                        ↓ Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Project Submission Area */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
        <h3 className="text-lg font-bold text-gray-800 mb-2">Project Submission</h3>
        <p className="text-xs text-gray-400 mb-6">
          Upload a file or submit a link (e.g. GitHub repo, Google Drive folder) with a short description for your instructor to review.
        </p>

        {loadingSubmission ? (
          <div className="h-10 bg-gray-100 rounded-xl animate-pulse" />
        ) : showForm ? (
          <form onSubmit={handleSubmitProject} className="flex flex-col gap-4">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] w-px h-px opacity-0 overflow-hidden"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Submission Title</label>
              <input
                type="text"
                placeholder="Submission Title"
                value={subForm.title}
                onChange={(e) => setSubForm({ ...subForm, title: e.target.value })}
                required
                className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white text-gray-800"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Submission Description</label>
              <textarea
                placeholder="Describe your project work or add any comments for the instructor..."
                value={subForm.description}
                onChange={(e) => setSubForm({ ...subForm, description: e.target.value })}
                required
                rows={4}
                className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white resize-none text-gray-800"
              />
            </div>
            <div>
              <div className="flex gap-2 mb-2">
                {[
                  { key: "file", label: "Upload File" },
                  { key: "link", label: "Paste Link" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setSubMode(t.key)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                      subMode === t.key
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {subMode === "file" ? (
                <>
                  <input
                    type="file"
                    accept={SUBMISSION_ACCEPTED_FILES}
                    onChange={(e) => setSubFile(e.target.files[0] || null)}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 cursor-pointer"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Max 10 MB. Notebooks (.ipynb) and Python (.py) accepted.
                    {submission?.file_path && !subFile && " Leave empty to keep your current file."}
                  </p>
                </>
              ) : (
                <input
                  type="url"
                  placeholder="e.g. https://github.com/yourusername/repo-name"
                  value={subForm.file_link}
                  onChange={(e) => setSubForm({ ...subForm, file_link: e.target.value })}
                  className="w-full border border-gray-200 py-3 px-4 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white text-gray-800"
                />
              )}
            </div>

            {submission && (
              <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-4 text-amber-800 text-xs">
                ⚠️ <strong>Note:</strong> Resubmitting will clear your current grade and reset the status to pending.
              </div>
            )}

            <div className="flex gap-3 justify-end mt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-xs sm:text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs sm:text-sm font-semibold hover:shadow-lg transition-all cursor-pointer disabled:opacity-50"
              >
                {saving ? "Saving..." : submission ? "Save Changes" : "Submit Project"}
              </button>
            </div>
          </form>
        ) : submission ? (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl p-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                <div>
                  <p className="text-sm font-bold text-gray-800">{submission.title || "Project Work"}</p>
                  <p className="text-xs text-gray-400">Submitted on {new Date(submission.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                {submission.file_download_url && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowSubmissionPreview((v) => !v)}
                      className="text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl transition-colors inline-block"
                    >
                      {showSubmissionPreview ? "Hide Preview" : "Preview File"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        downloadSubmissionFile(submission).catch(() =>
                          toast.error("Could not download this file.")
                        )
                      }
                      className="text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl transition-colors inline-block"
                    >
                      Download File
                    </button>
                  </>
                )}
                {submission.file_link && (
                  <a
                    href={submission.file_link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl transition-colors inline-block"
                  >
                    View Link
                  </a>
                )}
                {!submission.grade && !closed && (
                  <button
                    onClick={() => {
                      setSubForm({
                        title: submission.title || "",
                        description: submission.description || "",
                        file_link: submission.file_link || "",
                      });
                      setSubMode(submission.file_path ? "file" : "link");
                      setSubFile(null);
                      startedAt.current = Date.now();
                      setShowForm(true);
                    }}
                    className="text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl transition-colors cursor-pointer"
                  >
                    Resubmit
                  </button>
                )}
              </div>
            </div>

            {showSubmissionPreview && submission.file_download_url && (
              <div className="px-1">
                <SubmissionFilePreview submission={submission} />
              </div>
            )}

            {submission.grade && (
              <div className="border border-green-200 bg-green-50/50 rounded-2xl p-5 flex items-start gap-4">
                <div className="text-3xl">🏆</div>
                <div>
                  <h4 className="font-bold text-green-950 text-sm">Graded: {submission.grade}</h4>
                  {submission.instructor_feedback && (
                    <div className="mt-2 text-xs text-green-800 whitespace-pre-wrap italic">
                      "{submission.instructor_feedback}"
                    </div>
                  )}
                  {submission.feedback_at && (
                    <p className="text-[10px] text-green-600 mt-2">
                      Feedback from instructor on {new Date(submission.feedback_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {!submission.grade && (
              <div className="border border-blue-100 bg-blue-50/40 rounded-2xl p-4 flex items-center gap-3">
                <div className="text-xl">⏳</div>
                <p className="text-xs text-blue-900 leading-normal">
                  Your project is pending review. You will see your grade and instructor feedback here once graded.
                </p>
              </div>
            )}
          </div>
        ) : closed ? (
          <div className="w-full border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <span className="text-2xl mb-2">🔒</span>
            <span className="text-sm font-semibold text-gray-500">Submission closed</span>
            <span className="text-xs text-gray-400 mt-1">The deadline for this project has passed.</span>
          </div>
        ) : (
          <button
            onClick={() => {
              setSubForm({ title: "", description: "", file_link: "" });
              setSubMode("link");
              setSubFile(null);
              startedAt.current = Date.now();
              setShowForm(true);
            }}
            className="w-full border-2 border-dashed border-gray-200 hover:border-blue-500 rounded-2xl p-8 flex flex-col items-center justify-center transition-all group cursor-pointer"
          >
            <span className="text-3xl text-gray-300 group-hover:text-blue-500 mb-2 transition-colors">+</span>
            <span className="text-sm font-semibold text-gray-500 group-hover:text-blue-500 transition-colors">Submit your project work</span>
            <span className="text-xs text-gray-400 mt-1">Upload a file or link your repository / drive, with a title and description</span>
          </button>
        )}
      </div>
    </div>
  );
}

function basename(path) {
  return (path || "").split("/").pop();
}

// ─── Assessment panel (full start → take → submit flow) ──────────────────────

// Also used by the new student course page (components/Dashboard/learn).
export function AssessmentPanel({ assessment }) {
  const [phase, setPhase]       = useState("info"); // info | taking | submitted
  const [meta, setMeta]         = useState(null);   // GET /assessments/{uuid}
  const [attempt, setAttempt]   = useState(null);   // start response
  const [answers, setAnswers]   = useState({});     // { [questionId]: optionId | optionId[] }
  const [result, setResult]     = useState(null);   // submit response
  const [timeLeft, setTimeLeft] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [accessBlocked, setAccessBlocked] = useState(null); // error message if 403

  // Load assessment state on mount / when assessment changes
  useEffect(() => {
    if (!assessment?.uuid || !assessment?.is_published) {
      setMetaLoading(false);
      return;
    }
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    client
      .get(`/assessments/${assessment.uuid}`)
      .then(({ data }) => {
        setMeta(data);
        setAccessBlocked(null);
        if (data.active_attempt) {
          // Resume in-progress attempt
          setAttempt({ attempt_uuid: data.active_attempt.uuid, questions: [] });
          setTimeLeft(data.active_attempt.seconds_remaining);
          setPhase("taking");
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message;
        if (err?.response?.status === 403) {
          setAccessBlocked(msg || "Complete your payment to access assessments.");
        }
      })
      .finally(() => setMetaLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment?.uuid]);

  // Countdown timer
  useEffect(() => {
    if (phase !== "taking" || timeLeft === null) return;
    if (timeLeft <= 0) {
      toast.warn("Time's up! Submitting your answers.");
      handleSubmit();
      return;
    }
    const t = setTimeout(() => setTimeLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeLeft]);

  const formatTime = (secs) => {
    if (secs == null) return "";
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleStart = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/assessments/${assessment.uuid}/start`);
      setAttempt(data);
      setAnswers({});
      setTimeLeft(data.seconds_remaining);
      setPhase("taking");
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not start assessment.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!attempt?.attempt_uuid) return;
    setBusy(true);
    try {
      const formatted = Object.entries(answers).map(([qId, val]) => ({
        question_id: parseInt(qId),
        ...(Array.isArray(val) ? { option_ids: val } : { option_id: val }),
      }));
      const { data } = await client.post(`/attempts/${attempt.attempt_uuid}/submit`, {
        answers: formatted,
      });
      setResult(data);
      setPhase("submitted");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not submit assessment.");
    } finally {
      setBusy(false);
    }
  };

  const selectAnswer = (qId, optId, isMultiple) => {
    setAnswers((prev) => {
      if (isMultiple) {
        const current = Array.isArray(prev[qId]) ? prev[qId] : [];
        return {
          ...prev,
          [qId]: current.includes(optId)
            ? current.filter((id) => id !== optId)
            : [...current, optId],
        };
      }
      return { ...prev, [qId]: optId };
    });
  };

  // Derived stats — computed before early returns so all guard screens can use them
  const info          = meta?.assessment || {};
  const lastAttempt   = meta?.last_attempt;
  const statQuestions = assessment.questions_count ?? info.question_count;
  const statTimeLimit = assessment.time_limit_minutes ?? info.time_limit_minutes;
  const statPassScore = assessment.passing_score ?? info.passing_score;

  // ── Not published ──
  if (!assessment?.is_published) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="text-xl font-bold text-gray-800 mb-1">{assessment?.title}</h2>
        <p className="text-sm text-gray-500">This assessment is not available yet.</p>
      </div>
    );
  }

  if (metaLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 flex justify-center">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-[#d15424] rounded-full animate-spin" />
      </div>
    );
  }

  if (accessBlocked) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">{assessment.title}</h2>
        <p className="text-sm text-gray-500 mb-6">{accessBlocked}</p>
        {/* Still show stats from blackboard data */}
        <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
          {[
            { label: "Questions",  value: statQuestions != null ? statQuestions : "—" },
            { label: "Time Limit", value: statTimeLimit ? `${statTimeLimit} min` : "—" },
            { label: "Pass Score", value: statPassScore ? `${statPassScore}%` : "—"   },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#001931]">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Result screen ──
  if (phase === "submitted" && result) {
    const passed = result.passed;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
        <div className={`rounded-xl p-6 text-center mb-6 ${passed ? "bg-green-50" : "bg-red-50"}`}>
          <div className="text-4xl mb-2">{passed ? "🎉" : "📚"}</div>
          <p className={`text-2xl font-bold mb-1 ${passed ? "text-green-700" : "text-red-600"}`}>
            {result.score}%
          </p>
          <p className={`font-semibold ${passed ? "text-green-700" : "text-red-600"}`}>
            {passed ? "You passed!" : "Not quite — keep studying!"}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Passing score: {result.passing_score}%
          </p>
        </div>

        {result.retake_available_at && (
          <p className="text-xs text-gray-500 text-center mb-4">
            Retake available after{" "}
            {format(safeDate(result.retake_available_at) || new Date(), "MMM d 'at' h:mm a")}
          </p>
        )}

        <button
          onClick={() => { setPhase("info"); setResult(null); setAttempt(null); }}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold
            py-2.5 rounded-xl text-sm transition-colors"
        >
          Back to Assessment
        </button>
      </div>
    );
  }

  // ── Taking screen ──
  if (phase === "taking" && attempt) {
    const questions = attempt.questions || [];

    if (questions.length === 0) {
      return (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
          <p className="text-gray-500 text-sm mb-4">
            You have an active attempt. Questions failed to load — please refresh the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#d15424] text-white px-5 py-2.5 rounded-xl text-sm font-semibold"
          >
            Refresh
          </button>
        </div>
      );
    }

    const answered = Object.keys(answers).length;

    return (
      <div className="flex flex-col gap-4">
        {/* Timer bar */}
        <div className="bg-white rounded-xl border border-gray-100 px-5 py-3
          flex items-center justify-between sticky top-4 z-10">
          <p className="font-semibold text-gray-700 text-sm">{assessment.title}</p>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {answered}/{questions.length} answered
            </span>
            {timeLeft != null && (
              <span className={`font-mono font-bold text-sm px-3 py-1 rounded-lg ${
                timeLeft < 300 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-700"
              }`}>
                {formatTime(timeLeft)}
              </span>
            )}
          </div>
        </div>

        {/* Questions */}
        {questions.map((q, qi) => {
          const isMultiple = q.type === "multiple";
          const selected = answers[q.id];
          return (
            <div key={q.id} className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
              <p className="font-semibold text-gray-800 mb-1 leading-snug">
                <span className="text-gray-400 mr-2">{qi + 1}.</span>
                {q.question_text}
              </p>
              {isMultiple && (
                <p className="text-xs text-blue-500 mb-3">Select all that apply</p>
              )}
              <div className="flex flex-col gap-2 mt-3">
                {(q.options || []).map((opt) => {
                  const isSelected = isMultiple
                    ? Array.isArray(selected) && selected.includes(opt.id)
                    : selected === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => selectAnswer(q.id, opt.id, isMultiple)}
                      className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl
                        border-2 transition-all text-sm ${
                        isSelected
                          ? "border-[#d15424] bg-[#d15424]/5 text-[#d15424] font-semibold"
                          : "border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center
                        justify-center ${isSelected ? "border-[#d15424] bg-[#d15424]" : "border-gray-300"}`}>
                        {isSelected && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                      </span>
                      {opt.option_text}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Submit */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center
          justify-between gap-4">
          <p className="text-sm text-gray-500">
            {questions.length - answered > 0
              ? `${questions.length - answered} question(s) unanswered`
              : "All questions answered ✓"}
          </p>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="bg-[#d15424] hover:bg-[#b8451d] disabled:opacity-60 text-white font-semibold
              px-6 py-2.5 rounded-xl text-sm transition-colors"
          >
            {busy ? "Submitting…" : "Submit Assessment"}
          </button>
        </div>
      </div>
    );
  }

  // ── Info screen (default) ──
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#d15424]/10 flex items-center justify-center
          text-2xl flex-shrink-0">
          📝
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">{assessment.title}</h2>
          {assessment.description && (
            <p className="text-gray-500 text-sm mt-1">{assessment.description}</p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Questions",  value: statQuestions != null ? statQuestions : "—" },
          { label: "Time Limit", value: statTimeLimit ? `${statTimeLimit} min` : "—" },
          { label: "Pass Score", value: statPassScore ? `${statPassScore}%` : "—"   },
        ].map((s) => (
          <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#001931]">{s.value}</p>
            <p className="text-xs text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Last attempt result */}
      {lastAttempt && (
        <div className={`rounded-xl p-4 mb-4 ${lastAttempt.passed ? "bg-green-50" : "bg-orange-50"}`}>
          <p className={`font-semibold text-sm ${lastAttempt.passed ? "text-green-700" : "text-orange-700"}`}>
            Last attempt: {lastAttempt.score}%  {lastAttempt.passed ? "✓ Passed" : "✗ Did not pass"}
          </p>
          {!lastAttempt.can_retake && lastAttempt.retake_available_at && (
            <p className="text-xs text-gray-500 mt-1">
              Retake available{" "}
              {format(safeDate(lastAttempt.retake_available_at) || new Date(), "MMM d 'at' h:mm a")}
            </p>
          )}
        </div>
      )}

      <button
        onClick={handleStart}
        disabled={busy || (lastAttempt && !lastAttempt.can_retake)}
        className="w-full bg-[#d15424] hover:bg-[#b8451d] disabled:opacity-50 disabled:cursor-not-allowed
          text-white font-semibold py-3 rounded-xl text-sm transition-colors"
      >
        {busy
          ? "Starting…"
          : lastAttempt
          ? lastAttempt.can_retake ? "Retake Assessment" : "Retake Not Yet Available"
          : "Start Assessment"}
      </button>
    </div>
  );
}

function OverviewPanel({ modules }) {
  const allSchedules = modules.flatMap((m) => [
    ...(m.direct_schedules || []),
    ...(m.weeks || []).flatMap((w) => w.schedules || []),
  ]);

  const live     = allSchedules.filter((s) => s.status === "live");
  const upcoming = allSchedules.filter((s) => s.status === "upcoming");
  const recorded = allSchedules.filter((s) => s.status === "recorded");
  const total    = allSchedules.length;

  const nextClass = upcoming.sort((a, b) => {
    const da = safeDate(a.start_time) || 0;
    const db = safeDate(b.start_time) || 0;
    return da - db;
  })[0];

  return (
    <div className="flex flex-col gap-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Sessions",  value: total,          color: "text-[#001931]" },
          { label: "Recorded",        value: recorded.length, color: "text-green-600" },
          { label: "Upcoming",        value: upcoming.length, color: "text-blue-600"  },
          { label: "Live Now",        value: live.length,     color: "text-red-500"   },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Live now */}
      {live.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
            <p className="font-semibold text-red-700 text-sm">Class in Progress</p>
          </div>
          {live.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-4">
              <p className="font-medium text-gray-800 text-sm">{s.topic}</p>
              <a
                href={s.link}
                target="_blank"
                rel="noreferrer"
                className="flex-shrink-0 bg-red-500 hover:bg-red-600 text-white text-sm
                  font-semibold px-4 py-2 rounded-xl transition-colors"
              >
                Join Now
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Next class */}
      {nextClass && live.length === 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-3">
            Next Class
          </p>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-gray-800">{nextClass.topic}</p>
              {safeDate(nextClass.start_time) && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {format(safeDate(nextClass.start_time), "EEEE, MMMM d · h:mm a")}
                </p>
              )}
            </div>
            <StatusBadge status={nextClass.status} />
          </div>
        </div>
      )}

      {/* Empty state */}
      {total === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <div className="text-5xl mb-3">📅</div>
          <p className="font-semibold text-gray-700 mb-1">No sessions scheduled yet</p>
          <p className="text-sm text-gray-400">Sessions will appear here once your cohort starts.</p>
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function CourseBlackboardPage() {
  const { uuid }     = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { userLocation, authState } = useContext(GlobalContext);
  const isNG         = userLocation?.data === "NG";
  const user         = authState?.data?.user;

  const searchParams = new URLSearchParams(location.search);
  const cohortId     = searchParams.get("cohort_id");

  const [blackboard,   setBlackboard]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(null);
  const [openMods,     setOpenMods]     = useState(new Set());
  const [openWeeks,    setOpenWeeks]    = useState(new Set());
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [uploadOpen,   setUploadOpen]   = useState(false);
  const [uploadId,     setUploadId]     = useState("");

  const findFreshSelected = (currentSelected, freshModules) => {
    if (!currentSelected || !freshModules) return null;
    const { type, id } = currentSelected;
    
    if (type === "lesson") {
      for (const mod of freshModules) {
        const direct = (mod.direct_schedules || []).find(s => s.id === id);
        if (direct) return { type: "lesson", id, data: direct };
        for (const week of (mod.weeks || [])) {
          const sched = (week.schedules || []).find(s => s.id === id);
          if (sched) return { type: "lesson", id, data: sched };
        }
      }
    } else if (type === "project") {
      for (const mod of freshModules) {
        const proj = (mod.projects || []).find(p => p.id === id);
        if (proj) return { type: "project", id, data: proj };
      }
    } else if (type === "assessment") {
      for (const mod of freshModules) {
        const assess = (mod.assessments || []).find(a => a.id === id);
        if (assess) return { type: "assessment", id, data: assess };
      }
    }
    return currentSelected;
  };

  const fetchBlackboard = async (isRefresh = false) => {
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const url = cohortId 
        ? `/courses/live/${uuid}/blackboard?cohort_id=${cohortId}`
        : `/courses/live/${uuid}/blackboard`;
      const { data } = await client.get(url);
      setBlackboard(data);

      if (!isRefresh) {
        // Auto-expand first module and its first week
        const firstMod = data.modules?.[0];
        if (firstMod) {
          setOpenMods(new Set([firstMod.id]));
          const firstWeek = firstMod.weeks?.[0];
          if (firstWeek) setOpenWeeks(new Set([firstWeek.id]));
        }
      } else if (selected) {
        const freshSelected = findFreshSelected(selected, data.modules);
        if (freshSelected) {
          setSelected(freshSelected);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      if (err?.response?.status === 403) {
        toast.error(msg || "You are not enrolled in this course.");
        navigate("/dashboard");
      } else {
        toast.error("Could not load course blackboard.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlackboard(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, cohortId]);

  const toggleMod = (id) =>
    setOpenMods((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleWeek = (id) =>
    setOpenWeeks((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleSelect = (item) => {
    setSelected(item);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-[#d15424] rounded-full animate-spin" />
      </div>
    );
  }

  if (!blackboard) return null;

  const { course, modules = [] } = blackboard;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-clip">
      {/* Assignment upload dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth>
        <UploadAnswers id={uploadId} onClose={() => setUploadOpen(false)} />
      </Dialog>

      {/* ── Hero ── */}
      <div style={{ background: "#001931" }} className="relative overflow-hidden">
        {course?.image_url && (
          <img
            src={course.image_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
        )}
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 pt-16 md:pt-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-gray-400 hover:text-white text-sm flex items-center gap-1.5 mb-5
              transition-colors"
          >
            ← My Courses
          </button>

          <div className="flex items-center gap-4">
            {course?.image_url && (
              <img
                src={course.image_url}
                alt={course.title}
                className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border-2 border-white/20"
              />
            )}
            <div>
              <span className="text-xs bg-[#d15424] text-white px-2.5 py-0.5 rounded-full
                font-semibold uppercase tracking-wide">
                Live Course
              </span>
              <h1 className="text-xl md:text-2xl font-bold text-white mt-1">
                {course?.title || "Course Blackboard"}
              </h1>
            </div>

            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="ml-auto lg:hidden bg-white/10 hover:bg-white/20 text-white px-3 py-2
                rounded-xl text-sm font-semibold transition-colors"
            >
              ☰ Modules
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      {user?.is_instructor ? (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <InstructorBlackboard
            blackboard={blackboard}
            user={user}
            cohortId={cohortId ? Number(cohortId) : undefined}
            onRefresh={() => fetchBlackboard(true)}
          />
        </div>
      ) : null}

      {!user?.is_instructor && (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex gap-6 items-start">

        {/* ── Collapsed Sidebar Toggle (desktop) ── */}
        {!desktopSidebarOpen && (
          <button
            onClick={() => setDesktopSidebarOpen(true)}
            className="hidden lg:flex items-center justify-center w-10 h-10 bg-white hover:bg-gray-50 text-gray-600
              border border-gray-200 rounded-xl shadow-sm transition-all sticky top-4 hover:border-gray-300 active:scale-95 group"
            title="Expand Course Content"
          >
            📂
          </button>
        )}

        {/* ── Sidebar (desktop) ── */}
        {desktopSidebarOpen && (
          <aside
            className="hidden lg:flex flex-col w-72 flex-shrink-0 rounded-2xl overflow-hidden
              sticky top-4 max-h-[calc(100vh-6rem)] shadow-sm border border-gray-200 bg-white"
          >
            {/* Header */}
            <div className="px-4 py-4 border-b border-gray-100 flex-shrink-0 bg-gray-50/50 flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-800 text-sm">Course Content</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">
                    {modules.length} module{modules.length !== 1 ? "s" : ""}
                  </span>
                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                  <span className="text-xs text-gray-400">
                    {modules.reduce((n, m) => n + (m.weeks || []).reduce((a, w) => a + (w.schedules?.length || 0), 0), 0)} sessions
                  </span>
                </div>
              </div>
              <button
                onClick={() => setDesktopSidebarOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200
                  text-gray-500 hover:text-gray-700 transition-colors text-xs font-bold"
                title="Collapse sidebar"
              >
                ←
              </button>
            </div>
            {/* Scrollable list */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {modules.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-400">
                  No modules added yet
                </div>
              ) : (
                modules.map((mod, i) => (
                  <SidebarModule
                    key={mod.id}
                    mod={mod}
                    index={i}
                    openMods={openMods}
                    openWeeks={openWeeks}
                    toggleMod={toggleMod}
                    toggleWeek={toggleWeek}
                    selected={selected}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </div>
          </aside>
        )}

        {/* ── Mobile sidebar drawer ──
            Backdrop and drawer are independent `fixed` siblings rather than the drawer being
            `absolute` inside a `fixed` wrapper — some Android WebViews fail to route touch-scroll
            gestures into a scrollable element nested two positioning-contexts deep inside a fixed
            ancestor, even though the CSS is spec-correct and programmatic scrolling still works
            (confirmed: scrollHeight > clientHeight, touch-action: auto throughout, but real-device
            swipe-to-scroll still failed). Flattening the positioning depth is the standard fix. */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <div
              className="fixed left-0 top-0 z-50 w-80 shadow-2xl flex flex-col bg-white border-r border-gray-100 lg:hidden"
              style={{ height: "100dvh" }}
            >
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100
                flex-shrink-0 bg-gray-50/50">
                <div>
                  <p className="font-bold text-gray-800 text-sm">Course Content</p>
                  <p className="text-xs text-gray-400 mt-0.5">{modules.length} modules</p>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100
                    hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                >
                  ✕
                </button>
              </div>
              <div
                className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {modules.map((mod, i) => (
                  <SidebarModule
                    key={mod.id}
                    mod={mod}
                    index={i}
                    openMods={openMods}
                    openWeeks={openWeeks}
                    toggleMod={toggleMod}
                    toggleWeek={toggleWeek}
                    selected={selected}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            </div>
          </>
        )}
        )}

        {/* ── Main content ── */}
        <main className="flex-1 min-w-0">
          {selected?.type === "lesson" && (
            <LessonPanel
              lesson={selected.data}
              isNG={isNG}
              user={user}
              onUpload={(id) => { setUploadId(id); setUploadOpen(true); }}
              onRefresh={() => fetchBlackboard(true)}
            />
          )}
          {selected?.type === "project" && (
            <ProjectPanel project={selected.data} />
          )}
          {selected?.type === "assessment" && (
            <AssessmentPanel assessment={selected.data} />
          )}
          {!selected && (
            <OverviewPanel modules={modules} />
          )}
        </main>
      </div>
      )}
    </div>
  );
}
